/**
 * 景點的「圖片與影音」區塊 — 對應窗屏檔案樹的四個插槽。
 *
 * 與上方的文字欄位不同，媒體掛載是**即時儲存**（獨立 API），不隨「儲存變更」
 * 一起送出：素材挑選本來就是一次一個動作，等表單送出反而容易誤以為沒存到。
 * 因此本區塊只在編輯既有景點時出現——新增時還沒有 spot id 可掛。
 *
 * ## 三語系共用
 *
 * 檔案樹雖然是 `assets/{roi}/{lang}/1_pic1.png` 逐語系一份，但實務上一階圖片與
 * 二階影片三語系常常是同一批檔——只有語音與文字真的分語言。原本這件事要開三次
 * 挑選器、在上千筆裡認出同一個檔三次，錯一次就是船上某個語系放到別的景點的圖。
 *
 * 勾了「三語系共用」之後，掛載與清空都同時套用到 zh／en／jp。實作上仍是逐語系
 * 各寫一列（後端模型不變），只是不必由人來重複三次。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from './Button';
import { MediaPicker } from './MediaPicker';
import {
  clearSpotMediaSlot,
  listSpotMedia,
  setSpotMediaSlot,
  type DisplayLocaleCode,
  type MediaType,
  type SpotMediaEntry,
  type SpotMediaList,
  type SpotMediaSlot,
} from '../lib/api-client';

/** CMS 語系 → 窗屏檔案樹語系。 */
export function toDisplayLocale(cmsLocale: string): DisplayLocaleCode {
  if (cmsLocale === 'en') return 'en';
  if (cmsLocale === 'ja') return 'jp';
  return 'zh';
}

const DISPLAY_LOCALES: readonly DisplayLocaleCode[] = ['zh', 'en', 'jp'];

export interface SlotSpec {
  slot: SpotMediaSlot;
  label: string;
  output: string;
  mediaType: MediaType;
  acceptMimes: readonly string[];
  multiple: boolean;
  hint?: string;
}

export const SLOTS: readonly SlotSpec[] = [
  {
    slot: 'poi_image',
    label: '一階圖片',
    output: '1_pic1.png … 1_picN.png',
    mediaType: 'image',
    acceptMimes: ['image/png'],
    multiple: true,
    hint: '張數決定 roiN.txt 的 poi_images 陣列長度，且三種語系必須張數相同',
  },
  {
    slot: 'intro_voice',
    label: '一階語音',
    output: '1_voice1.wav',
    mediaType: 'audio',
    acceptMimes: ['audio/wav', 'audio/x-wav', 'audio/wave'],
    multiple: false,
  },
  {
    slot: 'detail_video',
    label: '二階影片',
    output: '2_video1.mp4',
    mediaType: 'video',
    acceptMimes: ['video/mp4'],
    multiple: false,
  },
  {
    slot: 'detail_voice',
    label: '二階語音',
    output: '2_voice1.wav',
    mediaType: 'audio',
    acceptMimes: ['audio/wav', 'audio/x-wav', 'audio/wave'],
    multiple: false,
  },
];

interface Props {
  spotId: string;
  /** CMS 語系（zh-TW / en / ja），內部轉成窗屏的 zh / en / jp */
  cmsLocale: string;
  localeLabel: string;
  /** 景點的窗屏編號；就地上傳時用來組資料夾標籤，未設定則不打標籤。 */
  roiId?: string;
  /** 區塊標題；分頁後一階與二階的插槽會拆成兩塊。 */
  title?: string;
  /** 只顯示這些插槽；省略則全部。 */
  only?: readonly SpotMediaSlot[];
}

/** 這個插槽目前是否三語系掛著同一組檔（順序也要相同——順序就是 1_pic1、1_pic2…）。 */
function sharedInData(data: SpotMediaList | undefined, slot: SpotMediaSlot): boolean {
  const per = DISPLAY_LOCALES.map((l) => (data?.slots?.[slot]?.[l] ?? []).map((e) => e.media_id));
  const [zh] = per;
  if (!zh || zh.length === 0) return false;
  return per.every((ids) => ids.length === zh.length && ids.every((id, i) => id === zh[i]));
}

export function SpotMediaFields({
  spotId,
  cmsLocale,
  localeLabel,
  roiId,
  title,
  only,
}: Props): JSX.Element {
  const qc = useQueryClient();
  const locale = toDisplayLocale(cmsLocale);
  const [picking, setPicking] = useState<SlotSpec | null>(null);
  /**
   * 使用者手動改過的共用開關；沒改過的插槽跟著資料現況走。
   *
   * 不能只用 state：切到別的景點或別的分頁時，開關要重新反映那個景點的實際狀態，
   * 而不是沿用上一次的勾選。
   */
  const [sharedOverride, setSharedOverride] = useState<Partial<Record<SpotMediaSlot, boolean>>>({});

  const query = useQuery({
    queryKey: ['spot.media', spotId],
    queryFn: () => listSpotMedia(spotId),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['spot.media', spotId] });
  };

  /**
   * 逐語系送，不併發：三個請求打同一個景點的同一個插槽，序列化的代價是三次往返，
   * 換來的是失敗時「哪一個語系沒寫成」是確定的。
   */
  const writeSlot = async (
    slot: SpotMediaSlot,
    mediaIds: string[],
    locales: readonly DisplayLocaleCode[],
  ): Promise<void> => {
    for (const l of locales) {
      await setSpotMediaSlot(spotId, slot, l, mediaIds);
    }
  };

  const setMutation = useMutation({
    mutationFn: (v: { slot: SpotMediaSlot; mediaIds: string[]; locales: DisplayLocaleCode[] }) =>
      writeSlot(v.slot, v.mediaIds, v.locales),
    onSuccess: () => {
      setPicking(null);
      invalidate();
    },
  });

  const clearMutation = useMutation({
    mutationFn: async (v: { slot: SpotMediaSlot; locales: DisplayLocaleCode[] }) => {
      for (const l of v.locales) {
        await clearSpotMediaSlot(spotId, v.slot, l);
      }
    },
    onSuccess: invalidate,
  });

  const data = query.data;
  const shown = only ? SLOTS.filter((s) => only.includes(s.slot)) : SLOTS;
  const entriesOf = (slot: SpotMediaSlot): SpotMediaEntry[] => data?.slots?.[slot]?.[locale] ?? [];
  const isShared = (slot: SpotMediaSlot): boolean =>
    sharedOverride[slot] ?? sharedInData(data, slot);
  /** 共用時三語系一起寫，否則只寫目前這一個。 */
  const targetLocales = (slot: SpotMediaSlot): DisplayLocaleCode[] =>
    isShared(slot) ? [...DISPLAY_LOCALES] : [locale];

  /**
   * 勾起「共用」的當下就把目前語系的內容推到另外兩個語系。
   *
   * 只改開關不動資料的話，使用者會看到勾了卻什麼都沒發生——而他要的正是「現在
   * 這一組，三個語系都用」。取消勾選不動資料：已經掛上去的三份是有效內容，
   * 靜默刪掉兩份比留著危險得多。
   */
  const toggleShared = (slot: SpotMediaSlot, next: boolean): void => {
    setSharedOverride((prev) => ({ ...prev, [slot]: next }));
    const ids = entriesOf(slot).map((e) => e.media_id);
    if (!next || ids.length === 0) return;
    const others = DISPLAY_LOCALES.filter((l) => l !== locale);
    setMutation.mutate({ slot, mediaIds: ids, locales: others });
  };

  /** 拖曳排序過重，先提供上移／下移——poi_image 通常只有 3~5 張。 */
  const move = (slot: SpotMediaSlot, from: number, to: number): void => {
    const ids = entriesOf(slot).map((e) => e.media_id);
    if (to < 0 || to >= ids.length) return;
    const next = [...ids];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved as string);
    setMutation.mutate({ slot, mediaIds: next, locales: targetLocales(slot) });
  };

  const removeAt = (slot: SpotMediaSlot, index: number): void => {
    const ids = entriesOf(slot)
      .map((e) => e.media_id)
      .filter((_, i) => i !== index);
    setMutation.mutate({ slot, mediaIds: ids, locales: targetLocales(slot) });
  };

  return (
    <fieldset
      style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 12, margin: '12px 0' }}
    >
      <legend style={{ fontSize: 13, fontWeight: 600 }}>
        {title ?? '圖片與影音'}（{localeLabel}）
      </legend>

      {query.isLoading && <div style={{ fontSize: 13 }}>載入中…</div>}
      {query.error && <div className="error-banner">{(query.error as Error).message}</div>}
      {setMutation.error && (
        <div className="error-banner">{(setMutation.error as Error).message}</div>
      )}

      {/* 張數不一致的警告只在含 poi_image 的區塊出現，否則「二階畫面」上會冒出
          一段跟這頁無關的一階圖片警告 */}
      {data && !data.poi_image_consistent && shown.some((s) => s.slot === 'poi_image') && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 4,
            padding: 8,
            fontSize: 12,
            marginBottom: 10,
          }}
        >
          ⚠ 各語系一階圖片張數不一致（中 {data.poi_image_counts.zh}／英 {data.poi_image_counts.en}
          ／日 {data.poi_image_counts.jp}）。roiN.txt 只有一份 poi_images
          清單，張數不齊會讓部分語系缺圖，發布前必須補齊。
        </div>
      )}

      {shown.map((spec) => {
        const entries = entriesOf(spec.slot);
        return (
          <div key={spec.slot} style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 500 }}>{spec.label}</span>
              <span style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                → {spec.output}
              </span>
              <Button
                variant="secondary"
                onClick={() => setPicking(spec)}
                disabled={setMutation.isPending}
              >
                {entries.length > 0 && !spec.multiple ? '更換' : '＋ 選擇素材'}
              </Button>
              {entries.length > 0 && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    clearMutation.mutate({ slot: spec.slot, locales: targetLocales(spec.slot) })
                  }
                  disabled={clearMutation.isPending}
                >
                  {isShared(spec.slot) ? '清空（三語系）' : '清空'}
                </Button>
              )}
              <label
                style={{
                  fontSize: 12,
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                }}
                title="勾選後，掛載與清空同時套用到中／英／日三個語系"
              >
                <input
                  type="checkbox"
                  checked={isShared(spec.slot)}
                  onChange={(e) => toggleShared(spec.slot, e.target.checked)}
                  disabled={setMutation.isPending || clearMutation.isPending}
                />
                三語系共用
              </label>
            </div>

            {entries.length === 0 ? (
              <div style={{ fontSize: 12, color: '#9ca3af' }}>尚未掛載</div>
            ) : (
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 12 }}>
                {entries.map((e, i) => (
                  <li key={e.id} style={{ marginBottom: 2 }}>
                    <span style={{ wordBreak: 'break-all' }}>{e.filename}</span>
                    {spec.multiple && (
                      <>
                        <button
                          type="button"
                          onClick={() => move(spec.slot, i, i - 1)}
                          disabled={i === 0 || setMutation.isPending}
                          style={{ marginLeft: 6, cursor: 'pointer' }}
                          title="上移"
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          onClick={() => move(spec.slot, i, i + 1)}
                          disabled={i === entries.length - 1 || setMutation.isPending}
                          style={{ marginLeft: 2, cursor: 'pointer' }}
                          title="下移"
                        >
                          ↓
                        </button>
                      </>
                    )}
                    <button
                      type="button"
                      onClick={() => removeAt(spec.slot, i)}
                      disabled={setMutation.isPending}
                      style={{ marginLeft: 4, cursor: 'pointer' }}
                      title="移除"
                    >
                      ✕
                    </button>
                  </li>
                ))}
              </ol>
            )}
            {spec.hint && (
              <small style={{ color: '#9ca3af', display: 'block', marginTop: 2 }}>
                {spec.hint}
              </small>
            )}
          </div>
        );
      })}

      {picking && (
        <MediaPicker
          open
          title={
            isShared(picking.slot)
              ? `選擇「${picking.label}」素材（三語系共用）`
              : `選擇「${picking.label}」素材（${localeLabel}）`
          }
          mediaType={picking.mediaType}
          acceptMimes={picking.acceptMimes}
          multiple={picking.multiple}
          initialSelected={entriesOf(picking.slot).map((e) => e.media_id)}
          /*
           * 就地上傳的歸檔位置與「從資料夾套用」掃描的位置一致（`{roi}/{lang}`），
           * 這樣同一個景點無論用哪一條路徑補素材，媒體庫看起來都是同一棵樹。
           */
          upload={{
            language: locale,
            tags: roiId && roiId.trim() !== '' ? [`${roiId.trim()}/${locale}`] : [],
          }}
          /*
           * 只在這個景點的資料夾裡挑。窗屏檔案樹每個景點的檔名都一樣，跨景點列出來
           * 分不出誰是誰。景點還沒有窗屏編號時無從限定，此時退回整個媒體庫。
           */
          {...(roiId && roiId.trim() !== '' ? { restrictToFolder: roiId.trim() } : {})}
          onConfirm={(ids) =>
            setMutation.mutate({
              slot: picking.slot,
              mediaIds: ids,
              locales: targetLocales(picking.slot),
            })
          }
          onClose={() => setPicking(null)}
        />
      )}
    </fieldset>
  );
}
